<template>
  <wiskModal :visible="visible" :title="translations.txtImageEditorTitle || 'Edit Image'" size="lg" @hide="cancel" hideHeaderExtra>
    <wiskLoading :loading="loading" />

    <div class="image-editor-container">
      <div class="controls">
          <b-button variant="outline-primary" @click="rotate(-90)" :disabled="loading" class="me-2">
            <icon name="wisk-undo" :scale="1" class="me-1" />
            {{ translations.txtImageEditorRotateLeft }}
          </b-button>
          <b-button variant="outline-primary" @click="rotate(90)" :disabled="loading">
            {{ translations.txtImageEditorRotateRight }}
            <icon name="wisk-redo" :scale="1" class="ms-1" />
          </b-button>
      </div>

      <div class="cropper-wrapper">
        <div v-if="!imageData" class="no-image">
          <p>No image loaded</p>
          <p>Image data: {{ imageData ? 'Loaded' : 'Not loaded' }}</p>
        </div>
        <cropper
          v-if="imageData"
          ref="cropper"
          :src="imageData"

          :moveImage="false"
          :resizeImage="false"
          :defaultBoundaries="'fit'"
          :background="false"
          :transitions="false"
          :defaultSize="getDefaultSize"
        />
        <!--
        :stencilProps="{
            handlers: {},
            lines: {},
            movable: false,
            resizable: false
          }"
        :stencilSize="getStencilSize"
          imageRestriction="none" -->
      </div>
    </div>

    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="cancel" :disabled="loading">
        {{ translations.txtGenericCancel || 'Cancel' }}
      </b-button>
      <b-button variant="primary" @click="save" :disabled="loading">
        {{ translations.txtGenericSave || 'Save' }}
      </b-button>
    </template>
  </wiskModal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

export default {
  name: 'ImageEditor',
  components: { Cropper },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    imageFile: {
      type: File,
      default: null
    }
  },
  emits: ['save', 'cancel'],
  data() {
    return {
      originalImageData: null,
      imageData: null,
      loading: false,
      imageMimeType: null
    }
  },
  computed: {
    ...mapState(['translations'])
  },
  methods: {
    ...mapActions(['notify']),
    getDefaultSize({ imageSize }) {
      return {
        width: imageSize.width,
        height: imageSize.height
      }
    },
    getStencilSize({ boundaries }) {
      console.log('boundaries', boundaries)
      return {
        width: boundaries.width,
        height: boundaries.height
      }
    },
    rotate(angle) {
      if (this.$refs.cropper) {
        try {
          this.$refs.cropper.rotate(angle)
          this.$nextTick(() => {
            this.$refs.cropper.refresh()
          })
        } catch {}
      }
    },
    async save() {
      try {
        this.loading = true
        const imageFileName = this.imageFile.name
        const { canvas } = this.$refs.cropper.getResult()
        const blobCreator = (callback) => {
          canvas.toBlob((blob) => {
            callback(blob)
          }, this.imageMimeType)
        }
        blobCreator((blob) => {
          const file = new File([blob], imageFileName, { type: blob.type })
          this.$emit('save', file)
        })
      } catch (error) {
        console.error('Error saving image:', error)
        this.notify({ type: 'error', message: 'Error saving image' })
      } finally {
        this.loading = false
      }
    },

    cancel() {
      this.$emit('cancel')
    },

    getMimeType(file, fallback = null) {
      const byteArray = (new Uint8Array(file)).subarray(0, 4)
        let header = ''
        for (let i = 0; i < byteArray.length; i++) {
          header += byteArray[i].toString(16)
        }
      switch (header) {
            case '89504e47':
                return 'image/png'
            case '47494638':
                return 'image/gif'
            case 'ffd8ffe0':
            case 'ffd8ffe1':
            case 'ffd8ffe2':
            case 'ffd8ffe3':
            case 'ffd8ffe8':
                return 'image/jpeg'
            default:
                return fallback
        }
    }
  },
  watch: {
    imageFile: {
      handler() {
        if (this.imageFile) {
          const reader = new FileReader()
          reader.onload = (e) => {
            this.imageData = e.target.result
            this.imageMimeType = this.getMimeType(e.target.result, this.imageFile.type)
          }
          reader.readAsDataURL(this.imageFile)
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>

.image-editor-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 500px;

  .controls {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem 0;
  }

  .cropper-wrapper {
    flex: 1;
    min-height: 400px;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    overflow: hidden;
    position: relative;

    .no-image {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: #6c757d;
      font-size: 1.25rem;
      text-align: center;
      padding: 2rem;

      p {
        margin-bottom: 0.5rem;
      }
    }

    :deep(.vue-advanced-cropper) {
      height: 100%;
      width: 100%;

      .cropper__image {
        max-width: none;
        max-height: none;
      }

      .cropper__stencil {
        border: 2px solid #007bff;
        background: rgba(0, 123, 255, 0.1);
      }

      .cropper__boundaries {
        background: #f8f9fa;
      }

      .cropper__area {
        overflow: visible;
      }
    }
  }
}

// Ensure the modal can accommodate the cropper
:deep(.modal-lg) {
  max-width: 800px;

  .modal-body {
    padding: 1rem;
  }
}
</style>
